import CloseIcon from '@mui/icons-material/Close';
import { Box, Chip, CircularProgress, IconButton, Tooltip } from '@mui/material';
import PropTypes from 'prop-types';

export const TagsPopoverContent = ({
  selectedTagsLineItem,
  loadingLineItems,
  formatTagText,
  handleRemoveTag,
  handleRemoveAllTags,
}) => {
  const isLoading = loadingLineItems.has(selectedTagsLineItem.lineItemId);
  const tags = (selectedTagsLineItem.tags || []).filter(
    (tag) => tag?.tagName !== 'BACKFILLED_SHIP_BY'
  );

  return (
    <Box sx={{ p: 1.5, width: '400px', position: 'relative' }}>
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'flex-start',
          minHeight: '56px',
          maxHeight: '200px',
          border: '1px solid rgba(0, 0, 0, 0.12)',
          borderRadius: '4px',
          bgcolor: '#fff',
          '&:hover': { borderColor: '#1976d2' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.75,
            flex: 1,
            maxHeight: '180px',
            overflowY: 'auto',
            p: 1,
            pr: '40px',
            mr: '-23px',
            '&::-webkit-scrollbar': { width: '6px' },
            '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '3px' },
            '&::-webkit-scrollbar-thumb': { background: '#888', borderRadius: '3px' },
            '&::-webkit-scrollbar-thumb:hover': { background: '#555' },
          }}
        >
          {tags.map((tag, index) => {
            const tagKey = tag?.tagName || '';
            const isSystemTag = tag?.isSystemTag;
            return (
              <Chip
                key={tagKey || index}
                label={formatTagText(tag)}
                size='small'
                onDelete={
                  !isSystemTag ? () => handleRemoveTag(selectedTagsLineItem, tag) : undefined
                }
                deleteIcon={!isSystemTag ? <CloseIcon /> : undefined}
                disabled={isLoading}
                sx={{
                  '& .MuiChip-label': { whiteSpace: 'nowrap' },
                  ...(isSystemTag && { opacity: 0.7, cursor: 'default' }),
                }}
              />
            );
          })}
        </Box>

        <Box
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            zIndex: 1,
          }}
        >
          <Tooltip title='Remove all tags' arrow placement='top'>
            <IconButton
              size='small'
              onClick={() => handleRemoveAllTags(selectedTagsLineItem)}
              disabled={isLoading}
              sx={{
                bgcolor: '#FFD54F',
                width: 32,
                height: 32,
                '&:hover': { bgcolor: '#FFC107' },
                '&.Mui-disabled': { bgcolor: '#F5F5F5', opacity: 0.6 },
              }}
            >
              <CloseIcon sx={{ fontSize: '18px', color: '#000', fontWeight: 'bold' }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2,
          }}
        >
          <CircularProgress size={27} />
        </Box>
      )}
    </Box>
  );
};

const tagShape = PropTypes.shape({
  tagName: PropTypes.string,
  isSystemTag: PropTypes.bool,
});

TagsPopoverContent.propTypes = {
  selectedTagsLineItem: PropTypes.shape({
    lineItemId: PropTypes.string.isRequired,
    tags: PropTypes.arrayOf(tagShape),
  }).isRequired,
  loadingLineItems: PropTypes.instanceOf(Set).isRequired,
  formatTagText: PropTypes.func.isRequired,
  handleRemoveTag: PropTypes.func.isRequired,
  handleRemoveAllTags: PropTypes.func.isRequired,
};

export const TagsTableCell = ({ lineItem, isLoading, formatTagText, handleTagsClick }) => {
  const allTags = (lineItem.tags || []).filter((tag) => tag?.tagName !== 'BACKFILLED_SHIP_BY');
  const systemTags = allTags.filter((tag) => tag.isSystemTag);
  const userTags = allTags.filter((tag) => !tag.isSystemTag);

  if (allTags.length === 0) {
    return null;
  }

  const handleClick = (event) => {
    if (!isLoading) {
      handleTagsClick(event, lineItem);
    }
  };

  const firstUserTag = userTags[0];
  const remainingUserTags = userTags.slice(1);

  return (
    <Box sx={{ display: 'inline-flex', flexDirection: 'column', gap: 0.5 }}>
      {systemTags.length > 0 && (
        <Box sx={{ display: 'inline-flex', flexWrap: 'wrap', gap: 0.5 }}>
          {systemTags.map((tag) => (
            <Chip
              key={tag.tagName}
              label={formatTagText(tag)}
              size='small'
              color={tag.tagName === 'URGENT' ? 'error' : 'default'}
              sx={{
                cursor: 'default',
                ...(tag.tagName !== 'URGENT' && { opacity: 0.7 }),
                ...(tag.tagName === 'URGENT' && { fontWeight: 600 }),
              }}
            />
          ))}
        </Box>
      )}

      {userTags.length > 0 && (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            cursor: isLoading ? 'default' : 'pointer',
            opacity: isLoading ? 0.6 : 1,
          }}
        >
          <Tooltip title='Click to manage tags' arrow placement='top'>
            <Chip
              label={formatTagText(firstUserTag)}
              size='small'
              disabled={isLoading}
              onClick={handleClick}
              sx={{
                maxWidth: '120px',
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              }}
            />
          </Tooltip>

          {remainingUserTags.length > 0 && (
            <Tooltip
              title={remainingUserTags.map((tag) => formatTagText(tag)).join(', ')}
              arrow
              placement='top'
            >
              <Chip
                label={`+${remainingUserTags.length}`}
                size='small'
                variant='outlined'
                disabled={isLoading}
                sx={{ minWidth: '40px', cursor: 'default' }}
              />
            </Tooltip>
          )}
        </Box>
      )}
    </Box>
  );
};

TagsTableCell.propTypes = {
  lineItem: PropTypes.shape({
    lineItemId: PropTypes.string,
    tags: PropTypes.arrayOf(tagShape),
  }).isRequired,
  isLoading: PropTypes.bool,
  formatTagText: PropTypes.func.isRequired,
  handleTagsClick: PropTypes.func.isRequired,
};

TagsTableCell.defaultProps = {
  isLoading: false,
};
